import moment from 'moment';
import { FC, useMemo, useState } from 'react';

import { Button } from '@common/components/Button';
import SectionContainer from '@common/components/SectionContainer';
import { UnavailableBox } from '@common/components/UnavailableBox';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { analytics } from '@features/analytics';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useOpenDatesFromQuery } from '@features/JourneyPickerV2/useOpenDatesFromQuery';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { CategoryFilterValue, useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import {
  buildCategoryOptions,
  UNCATEGORIZED_VALUE,
} from '@features/spa/utils/buildCategoryOptions';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { SpaClassListTemplate, ViewMode } from 'voyager';

import { groupClassesByTimeOfDay } from './helpers/groupClassesByTimeOfDay';
import { useSpaClassesList } from './hooks/useSpaClassesList';
import { EmptyStateWrapper } from './SpaClassesV2.styles';
import { SpaClassesV2Props } from './SpaClassesV2.types';

export const SpaClassesV2: FC<SpaClassesV2Props> = ({ property, journeyParams, language }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [view, setView] = useState<ViewMode>('compact');

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();
  const handleSearchComplete = useSpaSearchComplete();

  useOpenDatesFromQuery();

  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_CLASSES_LIST,
  });

  const selectedDate = moment(journeyParams.spaDate || journeyParams.fromDate).format('YYYY-MM-DD');

  const categoryQuery = router.query.category as string | undefined;
  const initialCategory: CategoryFilterValue =
    categoryQuery === UNCATEGORIZED_VALUE
      ? UNCATEGORIZED_VALUE
      : categoryQuery
      ? Number(categoryQuery)
      : null;

  const filter = useSpaEntityFilter({
    categoryId: initialCategory,
    arrivalDate: router.query.arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    guests: journeyParams?.spaAdults,
  });

  const {
    listItems,
    classesById,
    timeslotsByKey,
    categories,
    uncategorizedItems,
    isInitialLoading,
    isRefetching,
  } = useSpaClassesList({
    propertyId: property.id,
    language,
    date: selectedDate,
    categoryFilterId: filter.categoryFilterId,
    categoryFilterValue: filter.categoryFilterValue,
  });

  const categoryOptions = buildCategoryOptions(
    categories,
    t('spa.package.allCategories'),
    uncategorizedItems.length > 0 ? t('spa.noCategory') : undefined
  );

  const emptyState = (
    <EmptyStateWrapper>
      <UnavailableBox
        title={t('common.unavailable')}
        body={t('spaClasses.unavailableDescription')}
        footer={
          <Button isPrimary isInline onClick={() => openJourneyPickerDates()}>
            {t('spaClasses.chooseAnotherDate')}
          </Button>
        }
      />
    </EmptyStateWrapper>
  );

  const handleSelectTimeslot = (timeslotKey: string) => {
    const [classId, startDateTime] = timeslotKey.split('|');
    const timeslot = timeslotsByKey[timeslotKey];
    const spaClass = classesById[Number(classId)];

    if (timeslot?.price) {
      analytics.track({
        event: 'view_item',
        params: {
          currency: timeslot.price.currencyCode,
          value: timeslot.price.amount,
          items: [
            {
              item_category: 'spa-classes',
              item_id: `${spaClass?.externalId}`,
              item_name: spaClass?.name,
            },
          ],
        },
      });
    }

    router.push({
      pathname: Routes.SPA_CLASSES_BOOKING,
      query: { id: classId, bookingStep: 'guests', start: startDateTime },
    });
  };

  const groups = useMemo(
    () =>
      view === 'compact'
        ? groupClassesByTimeOfDay(listItems, (item) => timeslotsByKey[item.id]?.startDateTime, {
            morning: t('spaClass.morning'),
            morningSub: t('spaClasses.morningSub'),
            afternoon: t('spaClass.afternoon'),
            afternoonSub: t('spaClasses.afternoonSub'),
            evening: t('spaClass.evening'),
            eveningSub: t('spaClasses.eveningSub'),
          })
        : undefined,
    [view, listItems, timeslotsByKey, t]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="spa"
            categories={navCategories}
            category="spa-classes"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <Spinner showDelayMessage isActive={isRefetching} />
        <SpaClassListTemplate
          items={listItems}
          onSelectItem={handleSelectTimeslot}
          isLoading={isInitialLoading}
          view={view}
          onViewChange={setView}
          groups={groups}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          emptyState={emptyState}
          titleLabel={spaClassesPage?.title || t('app.spaClasses')}
          categoryLabel={t('common.category')}
          soldOutLabel={t('spaClasses.classFull')}
          noResultsLabel={t('app.noItemsFound')}
          noImageLabel={t('spa.noImage')}
          gridViewLabel={t('spaClasses.viewToggle.grid')}
          compactViewLabel={t('spaClasses.viewToggle.compact')}
          formatSpotsLeft={(spots) => t('spaClasses.spaces', { number: spots })}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
